import { calculateEntryAmount } from '@/services/utils';
angular.module('leanlaw.core').factory('entriesManager', ['$q', 'leanLawApi', 'alerts', 'singleRequest', 'onboardService', function ($q, leanLawApi, alerts, singleRequest, onboardService) {
  const _services = {
    create,
    list,
    listForMatter,
    listForClient,
    get: getEntryById,
    getEntryInstance,
    includeShortDescriptions,
    shortDescription,
    newDefaultEntry,
    compareEntries
  };

  /////////////////////////////

  const _list = {
    items: [],
    total: 0
  };
  let _date;
  let _tasks;
  const Entry = function () {};
  Entry.prototype.load = function (e) {
    angular.copy(e, this);
    this.startTime = new Date(e.startTime);
    this.endTime = new Date(e.endTime);
    this.timespan = `${time(this.startTime)} - ${time(this.endTime)}`;
    this.date = new Date(this.startTime);
    this.date.setHours(0);
    this.date.setMinutes(0);
    return this;
  };

  // Force
  Entry.prototype.update = function (update, force) {
    const entry = this;
    return leanLawApi.updateEntry({
      id: entry.id,
      body: entryBody(update || entry),
      force
    }).then(e => {
      entry.load(e);
      _list.items.sort((a, b) => a.startTime - b.startTime);
      alerts.info('Updated entry');
      return entry;
    }).catch(alerts.error);
  };
  Entry.prototype.delete = function () {
    const entry = this;
    return leanLawApi.deleteEntry({
      id: entry.id
    }).then(() => {
      // remove it from list if we have it
      for (let i = 0; i < _list.items.length; i++) {
        if (_list.items[i].id === entry.id) {
          _list.total -= entry.hours;
          _list.items.splice(i, 1);
          return;
        }
      }
    }).catch(alerts.error);
  };
  function list(request, requestName) {
    return singleRequest.apiCall(requestName).listEntries(request).then(list => {
      if (!list) return;
      return {
        list: list.entries.map(e => new Entry().load(e)),
        pagination: list.pagination,
        summary: list.summary
      };
    });
  }
  function listForMatter(matter, request) {
    if (!request) request = {};
    request.id = matter.id;
    return leanLawApi.listMatterEntries(request).then(list => _.map(list.entries, e => new Entry().load(e)));
  }
  function listForClient(client, request) {
    if (!request) request = {};
    request.id = client.id;
    return leanLawApi.listClientEntries(request).then(list => _.map(list.entries, e => new Entry().load(e)));
  }
  function time(date) {
    return date.toLocaleTimeString().replace(/([\d]+:[\d]{2})(:[\d]{2})(.*)/, '$1$3');
  }
  function create(create) {
    create.startTime = create.startTime || new Date();
    return leanLawApi.createEntry({
      body: entryBody(create)
    }).then(e => {
      const entry = new Entry().load(e);
      if (entry.date == _date) {
        // update list if the day matches
        _list.items.push(entry);
        _list.total += entry.hours;
        _list.items.sort((a, b) => a.startTime - b.startTime);
      }
      onboardService.reload();
      return entry;
    });
  }
  function entryBody(entry) {
    const body = angular.copy(entry);
    if (body.client) {
      delete body.client;
    }
    if (body.matter) {
      body.matter = {
        id: body.matter.id
      };
    }
    return body;
  }
  function getEntryInstance(e) {
    return new Entry().load(e);
  }
  function getEntryById(id) {
    return _list.items.find(e => e.id == id);
  }
  function roundedTime(t, defaultHourValue) {
    t = new Date(t);
    t.setMinutes(Math.floor(t.getMinutes() / (60 * defaultHourValue)) * (60 * defaultHourValue));
    t.setSeconds(0);
    return t;
  }
  function newDefaultEntry(init, item, defaultHourValue) {
    const entry = angular.copy(init) || {};
    entry.source = 'myleanlaw';

    // Story #41530: Skip time rounding for entries with localDateTime
    // Problem: Table displays raw localDateTime.time (e.g., "12:47 PM") but the form
    // was showing rounded time (e.g., "12:42 PM") because roundedTime() rounds to
    // the increment and endTime gets recalculated from rounded startTime + hours.
    // Fix: Preserve original times for localDateTime entries - entryEdit.js will
    // set the correct times using getEntryTimeDates() with localDateTime support.
    const hasLocalDateTime = !!entry.localDateTime?.time;
    if (hasLocalDateTime) {
      entry.startTime = new Date(entry.startTime);
      entry.endTime = new Date(entry.endTime);
    } else {
      entry.startTime = roundedTime(entry.startTime || new Date(), defaultHourValue);
    }
    if (item) entry.item = item;
    entry.description = entry.description || '';
    entry.hours = entry.hours || defaultHourValue || 0.1;
    if (!hasLocalDateTime) {
      entry.endTime = new Date(entry.startTime.getTime() + entry.hours * 60 * 60 * 1000);
    }
    if (!entry.matter) {
      entry.billingType = entry.billingType || 'billable';
    } else {
      if (!entry.billingType) {
        entry.billingType = entry.matter.billing.type === 'hours' ? 'billable' : entry.matter.billing.type === 'fixed' ? 'fixedFee' : 'nonBillable';
      }
    }
    if (!entry.date) {
      entry.date = new Date(entry.startTime);
      entry.date.setHours(0);
      entry.date.setMinutes(0);
      entry.date.setSeconds(0);
    }

    // Add amount field to entries for table
    if (entry.rate) {
      entry.amount = calculateEntryAmount(entry.rate.amount, entry.hours);
    }
    return entry;
  }

  // Returns a crunched-down version of an entry with the modifiable details
  // This is used to determine if the entry has been changed
  function getSimplified(entry, ignoreStartStop) {
    // These are the only fields we want to compare
    let compare = {
      id: entry.id || null,
      description: entry.description,
      matter: entry.matter ? entry.matter.id : null,
      client: entry.matter?.client?.id,
      date: entry.date || new Date(entry.startDate) || null,
      user: entry.user ? entry.user.id : null,
      customFlag: entry.customFlag,
      hours: entry.hours || 0,
      timekeeper: entry.account ? entry.account.id : null,
      activity: entry.codes ? entry.codes.activity : null,
      task: entry.codes ? entry.codes.task : null,
      rate: entry.rate && entry.billingType == 'billable' ? entry.rate.label : null,
      rateAmount: entry.rate && entry.billingType == 'billable' ? entry.rate.amount : null,
      billingType: entry.billingType || null,
      chargeId: entry.chargeId || null,
      note: entry.note || null
    };
    if (!ignoreStartStop) {
      compare = Object.assign(compare, {
        startTime: entry.startTime || null,
        endTime: entry.endTime || null
      });
    }
    return compare;
  }

  // Returns whether the selected/new entry has been modified by the user
  function compareEntries(a, b, ignoreStartStop) {
    const aSimple = getSimplified(a, ignoreStartStop);
    const bSimple = getSimplified(b, ignoreStartStop);
    return !angular.equals(aSimple, bSimple);
  }

  // <hack> This is for Woodsmall who needs a report that strips out
  // time entry detail. A better way of doing this is to have two
  // fields in the time entry, one for the "task" or activity and one
  // for the description detail

  let _tasksLoaded;
  function includeShortDescriptions() {
    // console.log("yo");
    if (!_tasksLoaded) {
      _tasksLoaded = leanLawApi.listTasks().then(list => {
        _tasks = list.tasks;
        _tasks.forEach(t => {
          t.key = strip(t.label);
        });
        _tasks = _tasks.sort((a, b) => a.key < b.key ? -1 : 1);
        return _tasks;
      });
    }
    return _tasksLoaded;
  }
  function strip(s) {
    return s.toLowerCase().replace(/[^a-z]/g, '');
  }
  const _special = [{
    key: 'fax',
    label: 'Fax'
  }, {
    key: 'email',
    label: 'Email'
  }, {
    key: 'telephone',
    label: 'Telephone'
  }, {
    key: 'phone',
    label: 'Telephone'
  }, {
    key: 'preparedraft',
    label: 'Prepare/Draft'
  }];
  function shortDescription(e) {
    const description = strip(e.description);
    let task = _tasks.find(t => description.indexOf(t.key) >= 0);
    if (!task) {
      task = _special.find(t => description.indexOf(t.key) >= 0);
    }
    return task ? task.label : e.description;
  }

  // </hack>

  return _services;
}]);